import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import {
  adminStatsService,
  UserDistributionByRole,
  UserDistributionByTenant,
} from '@/services/adminStats.service';

interface UserDistributionProps {
  className?: string;
}

// Pie Chart Component for Role Distribution
interface PieChartProps {
  data: UserDistributionByRole[];
  isLoading?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({ data, isLoading }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-32 h-32 rounded-full border-8 border-secondary-200 animate-pulse" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-secondary-500">No data available</p>
      </div>
    );
  }

  const colors = [
    { fill: '#3b82f6', light: '#dbeafe' }, // blue - Owner
    { fill: '#10b981', light: '#d1fae5' }, // green - Manager
    { fill: '#f59e0b', light: '#fef3c7' }, // amber - Broker
    { fill: '#8b5cf6', light: '#ede9fe' }, // purple - Admin
  ];

  const total = data.reduce((sum, d) => sum + d.count, 0);
  const size = 120;
  const center = size / 2;
  const radius = 50;
  const innerRadius = 30;

  let currentAngle = 0;

  const segments = data.map((d, i) => {
    const angle = (d.count / total) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;
    return {
      ...d,
      color: colors[i % colors.length],
      startAngle,
      angle,
    };
  });

  const getPath = (startAngle: number, angle: number, r: number, ir: number) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((startAngle + angle - 90) * Math.PI) / 180;

    const x1 = center + r * Math.cos(startRad);
    const y1 = center + r * Math.sin(startRad);
    const x2 = center + r * Math.cos(endRad);
    const y2 = center + r * Math.sin(endRad);
    const x3 = center + ir * Math.cos(endRad);
    const y3 = center + ir * Math.sin(endRad);
    const x4 = center + ir * Math.cos(startRad);
    const y4 = center + ir * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${ir} ${ir} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  return (
    <div className="flex items-center justify-between">
      <div className="relative">
        <svg width={size} height={size}>
          {segments.map((segment, i) => (
            <path
              key={i}
              d={getPath(segment.startAngle, segment.angle, radius, innerRadius)}
              fill={segment.color.fill}
              className="transition-all duration-200 cursor-pointer"
              style={{
                transform: hoveredIndex === i ? 'scale(1.05)' : 'scale(1)',
                transformOrigin: 'center',
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl font-bold text-secondary-900">{total.toLocaleString()}</p>
            <p className="text-xs text-secondary-500">Users</p>
          </div>
        </div>
      </div>

      <div className="flex-1 ml-6 space-y-2">
        {segments.map((segment, i) => (
          <div
            key={i}
            className={`flex items-center justify-between p-2 rounded-lg transition-colors cursor-pointer ${
              hoveredIndex === i ? 'bg-secondary-50' : ''
            }`}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: segment.color.fill }}
              />
              <span className="text-sm text-secondary-700">{segment.role}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-semibold text-secondary-900">
                {segment.count.toLocaleString()}
              </span>
              <span className="text-xs text-secondary-500 ml-1">
                ({segment.percentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Horizontal Bar Chart for Tenant Distribution
interface BarChartProps {
  data: UserDistributionByTenant[];
  isLoading?: boolean;
}

const HorizontalBarChart: React.FC<BarChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center justify-between mb-1">
              <div className="h-4 w-24 bg-secondary-200 rounded" />
              <div className="h-4 w-12 bg-secondary-200 rounded" />
            </div>
            <div className="h-4 bg-secondary-200 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-secondary-500">No data available</p>
      </div>
    );
  }

  const maxCount = Math.max(...data.map((d) => d.userCount), 1);

  const colors = [
    'bg-primary-500',
    'bg-success-500',
    'bg-warning-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-rose-500',
  ];

  return (
    <div className="space-y-4">
      {data.map((tenant, i) => {
        const widthPercentage = (tenant.userCount / maxCount) * 100;

        return (
          <div key={tenant.tenantId}>
            <div className="flex items-center justify-between mb-1">
              <Link
                to={`/tenants/${tenant.tenantId}`}
                className="text-sm font-medium text-secondary-700 hover:text-primary-600 transition-colors truncate max-w-40"
              >
                {tenant.tenantName}
              </Link>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-secondary-900">
                  {tenant.userCount}
                </span>
                <span className="text-xs text-secondary-400">
                  ({tenant.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="w-full h-4 bg-secondary-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  colors[i % colors.length]
                }`}
                style={{ width: `${widthPercentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const UserDistribution: React.FC<UserDistributionProps> = ({ className = '' }) => {
  const [roleData, setRoleData] = useState<UserDistributionByRole[]>([]);
  const [tenantData, setTenantData] = useState<UserDistributionByTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'role' | 'tenant'>('role');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [roles, tenants] = await Promise.all([
          adminStatsService.getUserDistributionByRole(),
          adminStatsService.getUserDistributionByTenant(10),
        ]);
        setRoleData(roles);
        setTenantData(tenants);
      } catch (err) {
        setError('Failed to load user distribution');
        console.error('Error fetching user distribution:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">User Distribution</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-48 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">User Distribution</h2>
            <p className="text-sm text-secondary-500">
              {activeTab === 'role' ? 'Users by role across platform' : 'Users per tenant'}
            </p>
          </div>
          <div className="flex items-center space-x-1 bg-secondary-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('role')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'role'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              By Role
            </button>
            <button
              onClick={() => setActiveTab('tenant')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'tenant'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              By Tenant
            </button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {activeTab === 'role' ? (
          <PieChart data={roleData} isLoading={isLoading} />
        ) : (
          <div>
            <HorizontalBarChart data={tenantData} isLoading={isLoading} />
            <div className="mt-4 pt-4 border-t border-secondary-100">
              <Link
                to="/users"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center"
              >
                View all users
                <svg
                  className="ml-1 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default UserDistribution;
